import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { generatePath, Link } from 'react-router-dom';

import { _getFile } from '@/api/files/queries';
import { useCreateGammaRoleRequest } from '@/api/gamma-role-requests/mutations';
import { RoleRequestType } from '@/api/gamma-role-requests/types';
import { _getGammaRoles } from '@/api/gamma-roles/queries';
import { _updateGammaTeamInvitation, _updateGammaTeamMember } from '@/api/gamma-team/mutations';
import {
  GAMMA_TEAM_QUERY_KEYS,
  GammaTeamRole,
  gammaTeamRole,
  selectableGammaTeamRoles,
} from '@/api/gamma-team/types';
import { _getProfileById } from '@/api/profile/queries';
import LinkedinIcon from '@/assets/svg/socials/linkedin-white.svg';
import XIcon from '@/assets/svg/socials/x-white.svg';
import ExternalLinkIcon from '@/assets/svg/ui-elements/external-link.svg';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { PagePaths } from '@/routes/page-paths';
import {
  Avatar,
  Drawer,
  IconButton,
  Input,
  Select,
  theme,
  Tooltip,
  Typography,
} from '@/shared/design-system';
import { normalizeExternalUrl } from '@/utils/normalize-external-url';

import {
  FieldsDivider,
  RegisteredUserCard,
  RegisteredUserHeadshot,
  RegisteredUserIdentity,
  RegisteredUserLinks,
  RegisteredUserSection,
  RegisteredUserText,
} from './gamma-team-member-edit.styled';
import { TeamMemberFields } from './team-member-fields.styled';

export const SUPERADMIN_ROLE_TOOLTIP = 'The Superadmin role cannot be revoked.';

export interface GammaTeamMemberEditData {
  email: string;
  role: GammaTeamRole;
  jobTitle: string | null;
  userId: string | null;
  invitationId: string | null;
  name: string | null;
  headshotFileId: string | null;
  isSuperAdmin: boolean;
}

export const GammaTeamMemberEdit = () => {
  const { open, handleClose, props } = useGlobalModal('GAMMA_TEAM_MEMBER_EDIT_MODAL');
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<{ role: GammaTeamRole | ''; jobTitle: string }>({
    role: '',
    jobTitle: '',
  });
  const isSuperAdmin = props?.isSuperAdmin ?? false;
  const isRegisteredUser = props?.userId !== null && props?.userId !== undefined;

  const { mutateAsync: updateMember } = useMutation({ mutationFn: _updateGammaTeamMember });
  const { mutateAsync: updateInvitation } = useMutation({ mutationFn: _updateGammaTeamInvitation });
  const { mutateAsync: requestRoleChange } = useCreateGammaRoleRequest();
  const roles = useQuery(_getGammaRoles(open && isRegisteredUser && !props?.invitationId));

  const { data: profileData } = useQuery({
    ..._getProfileById(props?.userId ?? undefined),
    enabled: open && isRegisteredUser,
  });

  const { data: headshotFile } = useQuery({
    ..._getFile(props?.headshotFileId as string),
    enabled: open && !!props?.headshotFileId,
  });

  const isFormChanged =
    props?.role !== formData.role || (props?.jobTitle ?? '') !== formData.jobTitle;

  useEffect(() => {
    if (props) {
      setFormData({
        role: props.role,
        jobTitle: props.jobTitle ?? '',
      });
    }
  }, [props]);

  const handleSave = async () => {
    const onSuccess = () => {
      handleCloseClick();
      queryClient.invalidateQueries({
        queryKey: [GAMMA_TEAM_QUERY_KEYS.dashboard],
      });
    };

    const body = {
      role: formData.role as GammaTeamRole,
      jobTitle: formData.jobTitle || null,
    };

    if (props?.invitationId) {
      await updateInvitation({ id: props.invitationId, ...body });
      onSuccess();
    } else if (props?.userId) {
      const roleChanged = props.role !== formData.role;
      const jobTitleChanged = (props.jobTitle ?? '') !== formData.jobTitle;

      if (roleChanged) {
        const oldRoleId = roles.data?.roles.find((role) => role.name === props.role)?.id;
        const newRoleId = roles.data?.roles.find((role) => role.name === formData.role)?.id;
        if (!oldRoleId || !newRoleId) return;

        await requestRoleChange({
          type: RoleRequestType.RemoveRole,
          payload: { roleId: oldRoleId, userId: props.userId },
        });
        await requestRoleChange({
          type: RoleRequestType.AssignRole,
          payload: { roleId: newRoleId, userId: props.userId },
        });
      }

      if (jobTitleChanged) {
        await updateMember({
          userId: props.userId,
          role: props.role,
          jobTitle: formData.jobTitle || null,
        });
      }

      onSuccess();
    }
  };

  const handleCloseClick = () => {
    setFormData({ role: '', jobTitle: '' });
    handleClose();
  };

  const roleOptions = isSuperAdmin
    ? [{ label: 'Superadmin', value: gammaTeamRole.Superadmin }, ...selectableGammaTeamRoles]
    : selectableGammaTeamRoles;

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => !next && handleCloseClick()}
      title="Edit Team Member"
      showClose
      secondaryAction={{ children: 'Cancel', onClick: handleCloseClick }}
      primaryAction={{
        children: 'Save',
        onClick: () => void handleSave().catch(() => undefined),
        disabled: !isFormChanged,
      }}
    >
      <TeamMemberFields>
        <Input size="lg" label="Email" required value={props?.email || ''} disabled />

        <Tooltip title={SUPERADMIN_ROLE_TOOLTIP} side="top" disabled={!isSuperAdmin}>
          <div>
            <Select
              size="lg"
              label="Role"
              required
              placeholder="Select role"
              options={roleOptions}
              value={formData.role || null}
              disabled={isSuperAdmin}
              onChange={(role) => setFormData((prev) => ({ ...prev, role: role as GammaTeamRole }))}
            />
          </div>
        </Tooltip>

        <Input
          size="lg"
          label="Gamma Job Title"
          placeholder="Enter gamma job title"
          value={formData.jobTitle}
          maxLength={100}
          onChange={(event) => setFormData((prev) => ({ ...prev, jobTitle: event.target.value }))}
        />

        {isRegisteredUser && (
          <>
            <FieldsDivider />

            <RegisteredUserSection>
              <Typography variant="body3">
                This user is already registered in Gamma Prime
              </Typography>

              <RegisteredUserCard>
                <RegisteredUserIdentity>
                  {headshotFile?.url ? (
                    <RegisteredUserHeadshot src={headshotFile.url} alt="" />
                  ) : (
                    <Avatar size="lg" initials={props?.name || '?'} />
                  )}
                  <RegisteredUserText>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {props?.name}
                    </Typography>
                    <Typography variant="body4" color={theme.colors.text.secondary} noWrap>
                      {props?.email}
                    </Typography>
                  </RegisteredUserText>
                </RegisteredUserIdentity>

                <RegisteredUserLinks>
                  <Tooltip title="Open profile" side="top">
                    <IconButton
                      size="sm"
                      color="tertiary"
                      variant="subtle"
                      aria-label="Open profile"
                      nativeButton={false}
                      // Navigation, so it is a link: Base UI would otherwise announce a button.
                      role="link"
                      render={
                        <Link
                          to={generatePath(PagePaths.userProfile, { id: props?.userId ?? '' })}
                        />
                      }
                    >
                      <ExternalLinkIcon />
                    </IconButton>
                  </Tooltip>

                  {profileData?.xProfile && (
                    <IconButton
                      size="sm"
                      color="tertiary"
                      variant="subtle"
                      nativeButton={false}
                      role="link"
                      render={
                        <a
                          href={normalizeExternalUrl(profileData.xProfile)}
                          aria-label="X profile"
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      }
                    >
                      <XIcon width={16} height={16} />
                    </IconButton>
                  )}

                  {profileData?.linkedinProfile && (
                    <IconButton
                      size="sm"
                      color="tertiary"
                      variant="subtle"
                      nativeButton={false}
                      role="link"
                      render={
                        <a
                          href={normalizeExternalUrl(profileData.linkedinProfile)}
                          aria-label="LinkedIn profile"
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      }
                    >
                      <LinkedinIcon width={16} height={16} />
                    </IconButton>
                  )}
                </RegisteredUserLinks>
              </RegisteredUserCard>
            </RegisteredUserSection>
          </>
        )}
      </TeamMemberFields>
    </Drawer>
  );
};
